import { CommonModule } from "@angular/common";
import { Component, HostBinding, Input } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DefectSeverityDistributionComponent } from "../../layouts/dashboard/test-report-summarization/defect-severity-distribution/defect-severity-distribution.component";
import { StackedChartComponent } from "../chart/stacked-chart/stacked-chart.component";
import { SharedComponents } from "../shared.module";

/**
 * Interface representing the structure of a processed component.
 */
interface ProcessedComponent {
  keyName: any;
  type: string;
  value: any;
  name: any;
  keyValue: any;
}

/**
 * Component for rendering a metric card with dynamic styles and custom classes.
 * Supports multiple metric types such as 'number_card' and 'key_value_card' with flexible design.
 */
@Component({
  selector: "app-metric-card-view",
  standalone: true,
  imports: [
    CommonModule,
    ...SharedComponents,
    StackedChartComponent,
    MatIconModule,
    MatTooltipModule,
    DefectSeverityDistributionComponent,
  ],
  templateUrl: "./metric-card-view.component.html",
  styleUrl: "./metric-card-view.component.scss",
})
export class MetricCardViewComponent {
  @Input()
  metric: any;

  @Input()
  customClass: any = {};

  bgColors = ["#FC404A", "#02F576"];
  rootStyles = getComputedStyle(document.documentElement);
  pieChartMeta: any = {
    showPercentageInLegend: true,
    isSeverity: true,
  };

  /**
   * Dynamic card body height style binding.
   * Returns a height based on the metric type (140px for 'number_card', 300px otherwise).
   */
  @HostBinding("style.--card-body-height")
  get cardBodyHeight(): string {
    switch (this.metric?.metricName) {
      case "testOutcomeDistribution":
        return "220px";
      case "summary":
        return "220px";
      case "testCaseSummary":
        return "705px";
      case "userStoryDetails":
        return "705px";
      case "reportedDefectCount":
        return "100%";
      case "defectSeverityDistribution":
        return "100%";
      case "defectList":
        return "auto";
      default:
        return "450px";
    }
  }

  /**
   * Dynamic card border color style binding.
   * Returns the border color based on the metric type.
   */
  @HostBinding("style.--card-border-color")
  get cardBorderColor(): string {
    return this.metric?.metricType === "number_card" || "key_value_card"
      ? this.rootStyles.getPropertyValue("--white").trim()
      : this.rootStyles.getPropertyValue("--coal-5").trim();
  }

  /**
   * Returns a custom CSS class string for the card based on the metric type.
   * Adds 'card shadow' and custom class if the metric type is not 'number_card' or 'key_value_card'.
   * @param metric The metric data used to determine the card's styling.
   * @returns A CSS class string for the card.
   */
  getCustomCardClass(metric: any) {
    if (metric.metricName == "applicationHotspots") {
      return "card applicationHotspots-content";
    } else if (metric.metricName == "testExecutionOverTime") {
      return `card testExecutionOvertimeContent`;
    } else if (
      metric.metricType !== "number_card" &&
      metric.metricType !== "key_value_card"
    ) {
      return `card ${this.customClass.card}`;
    } else {
      return "";
    }
  }

  getCustomeCSS(metric: any): any {
    switch (metric) {
      case "testExecutionOverTime":
        return `testExecutionOverTime`;
      default:
        return "";
    }
  }

  getCustomeClassForWrapper(metricName: string): any {
    switch (metricName) {
      case "reportedDefectCount":
        return `reportedDefectCount-wrapper`;
      case "defectSeverityDistribution":
        return `defectSeverityDistribution-wrapper`;
      case "topDefectCategories":
        return "topDefectCategories-wrapper";
      case "mostFailedTestCases":
        return "mostFailedTestCases-wrapper";
      case "riskIndications":
        return "riskIndications-wrapper";
      // case "userStoryDetails":
      //   return 'col-md-6 mx-0';
      //   break;
      default:
        return "";
    }
  }

  getMetricClass(_displayName: string): string {
    return '';
  }

  get accordionSections(): any[] {
    if (this.metric?.metricType !== 'expandableDetailedView') return [];
    return (this.metric?.data ?? []).map((item: any, i: number) => ({
      id: item.defect_id ?? i,
      name: item.defect_id,
      testcaseCount: item.affected_test_cases,
      paradigm: item.severity,
      tableColumns: [
        { display_name: 'Status', key: 'status' },
        { display_name: 'Defect Description', key: 'defect_description' },
        { display_name: 'Affected Test Cases', key: 'affected_test_cases' },
      ],
      tableRows: [item],
      expanded: false,
    }));
  }

  get accordionPageSize(): number {
    return this.metric?.meta?.[0]?.maxRows ?? 10;
  }
}
